import React from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FormModal from "../../components/FormModal";
import SelectFarmWarehouse from "../../components/SelectFarmWarehouse";
import DatePicker from "../../shared/DatePicker";
import OnShoreDisplay from "../OysterPL8/components/OnShoreDisplay";
import HatcheryConditionReportModal from "./Forms/HatcheryConditionReportModal";
import { getAPIDataParamsReturn, postApi } from "../../utils/apiFunction";
import { dateFormatZeros, textfieldDateFormat } from "../../utils/dateFunctions";

const Hatchery = (props) => { 
    const [farm, setFarm] = React.useState(null);
    const [leases, setLeases] = React.useState([]);
    const [lines, setLines] = React.useState([]);
    const [groups, setGroups] = React.useState([]);
    const [latestConditionReportByGroupId, setLatestConditionReportByGroupId] = React.useState({});
    const [batchColours, setBatchColours] = React.useState([]);
    const [colourMode, setColourMode] = React.useState("grade");
    const [loading, setLoading] = React.useState(false);
    const [reloadToken, setReloadToken] = React.useState(0);
    const [screenSelection, setScreenSelection] = React.useState(null);
    const [tooltipPosition, setTooltipPosition] = React.useState(null);
    const [tooltipLine, setTooltipLine] = React.useState(null);
    const [openDeployModal, setOpenDeployModal] = React.useState(false);
    const [pendingDeployRange, setPendingDeployRange] = React.useState(null);
    const [openConditionReportModal, setOpenConditionReportModal] = React.useState(false);
    const [pendingConditionGroupIds, setPendingConditionGroupIds] = React.useState([]);
    const selectionPopoverContentRef = React.useRef(null);
    const [actionDate, setActionDate] = React.useState(new Date());
    const [viewportWidth, setViewportWidth] = React.useState(typeof window !== "undefined" ? window.innerWidth : 1280);
    const isMobileViewport = viewportWidth < 576;
    const fixedScreenSize = 30;
    const fixedScreenGap = 6;
    const lengthScaleStops = [
        { ratio: 0, colour: "#d7191c" },
        { ratio: 0.2, colour: "#f07c22" },
        { ratio: 0.4, colour: "#f4c430" },
        { ratio: 0.6, colour: "#1a9850" },
        { ratio: 0.8, colour: "#2c7bb6" },
        { ratio: 1, colour: "#6a00a8" }
    ];
    const lengthScaleGradient = `linear-gradient(90deg, ${lengthScaleStops.map((stop) => `${stop.colour} ${stop.ratio * 100}%`).join(", ")})`;
    const deploymentDateScaleStops = [
        { ratio: 0, colour: "#d7191c" },
        { ratio: 0.25, colour: "#fdae61" },
        { ratio: 0.5, colour: "#ffffbf" },
        { ratio: 0.75, colour: "#abd9e9" },
        { ratio: 1, colour: "#2c7bb6" }
    ];
    const deploymentDateScaleGradient = `linear-gradient(90deg, ${deploymentDateScaleStops.map((stop) => `${stop.colour} ${stop.ratio * 100}%`).join(", ")})`;

    React.useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    React.useEffect(() => {
        const loadHatcheryData = async () => {
            if (!farm?.warehouseId) {
                setLeases([]);
                setLines([]);
                setGroups([]);
                setLatestConditionReportByGroupId({});
                setScreenSelection(null);
                setTooltipPosition(null);
                setTooltipLine(null);
                return;
            }

            setLoading(true);
            try {
                const [leaseData, lineData, groupData, colourData] = await Promise.all([
                    getAPIDataParamsReturn("/Farms/Lookups/Leases", { WarehouseId: farm.warehouseId }),
                    getAPIDataParamsReturn("/Farms/Lookups/Lines", { WarehouseId: farm.warehouseId }),
                    getAPIDataParamsReturn("/Farms/Groups", { WarehouseId: farm.warehouseId }),
                    getAPIDataParamsReturn("/Farms/Lookups/BatchColours", {})
                ]);
                setLeases(leaseData || []);
                setLines((lineData || []).filter((line) => (line?.status ?? 0) === 0));
                const filteredGroups = (groupData || []).filter((group) => !!group?.location?.lineId);
                setGroups(filteredGroups);
                setBatchColours(colourData || []);

                const selectedGroupIds = filteredGroups
                    .map((group) => group?.id)
                    .filter((groupId) => !!groupId);

                if (selectedGroupIds.length > 0) {
                    try {
                        const reportData = await getAPIDataParamsReturn("/Farms/Groups/ConditionReports", { GroupIds: selectedGroupIds });
                        const latestByGroupId = {};

                        (reportData || []).forEach((report) => {
                            const hasRecordedLength = report?.length !== null
                                && report?.length !== undefined
                                && report?.length !== ""
                                && Number.isFinite(Number(report.length));
                            if (!report?.groupId || latestByGroupId[report.groupId] || !hasRecordedLength) {
                                return;
                            }

                            latestByGroupId[report.groupId] = report;
                        });

                        setLatestConditionReportByGroupId(latestByGroupId);
                    } catch (error) {
                        console.error(error);
                        setLatestConditionReportByGroupId({});
                    }
                } else {
                    setLatestConditionReportByGroupId({});
                }
            } catch (error) {
                console.error(error);
                setLeases([]);
                setLines([]);
                setGroups([]);
                setLatestConditionReportByGroupId({});
                setBatchColours([]);
            } finally {
                setLoading(false);
            }
        };

        loadHatcheryData();
    }, [farm?.warehouseId, reloadToken]);

    const linesByLease = React.useMemo(() => {
        return lines.reduce((acc, line) => {
            const leaseId = line?.leaseId;
            if (!leaseId) {
                return acc;
            }
            if (!acc[leaseId]) {
                acc[leaseId] = [];
            }
            acc[leaseId].push(line);
            return acc;
        }, {});
    }, [lines]);

    const groupsByLine = React.useMemo(() => {
        return groups.reduce((acc, group) => {
            const lineId = group?.location?.lineId;
            if (!lineId) {
                return acc;
            }
            if (!acc[lineId]) {
                acc[lineId] = [];
            }
            acc[lineId].push(group);
            return acc;
        }, {});
    }, [groups]);

    const sortLines = (leaseLines) => {
        return [...leaseLines].sort((a, b) => {
            return (a?.lineNbr ?? 0) - (b?.lineNbr ?? 0);
        });
    };

    const sortGroupScreens = (lineGroups) => {
        return [...lineGroups].sort((a, b) => {
            const aStart = a?.location?.start ?? 0;
            const bStart = b?.location?.start ?? 0;
            if (aStart !== bStart) {
                return aStart - bStart;
            }
            const aEnd = a?.location?.end ?? 0;
            const bEnd = b?.location?.end ?? 0;
            return aEnd - bEnd;
        });
    };

    const lineLabel = (line) => {
        if (line?.name?.trim()) {
            return line.name.trim();
        }
        if (line?.lineNbr) {
            return `Tank ${line.lineNbr}`;
        }
        return `Tank ${line?.id}`;
    };

    const leaseLabel = (lease) => {
        if (lease?.name) {
            return lease.name;
        }
        if (lease?.number) {
            return `Lease ${lease.number}`;
        }
        return `Lease ${lease?.id}`;
    };

    const getBatchColour = (group) => {
        const key = group?.cropLabel;
        return batchColours.find((c) => c.crop === key)?.colour;
    };

    const getGradeColour = (group) => {
        return group?.grade?.colour || group?.grade?.color;
    };

    const getDeploymentTimestamp = (group) => {
        const rawDate = group?.location?.deployed ?? group?.location?.Deployed;
        if (!rawDate) {
            return null;
        }

        const date = new Date(rawDate);
        const time = date.getTime();
        if (!Number.isFinite(time) || date.getFullYear() <= 1) {
            return null;
        }

        return time;
    };

    const getDeploymentDateLabel = (group) => {
        const timestamp = getDeploymentTimestamp(group);
        return timestamp ? dateFormatZeros(new Date(timestamp)) : "Unknown";
    };

    const lengthScaleRange = React.useMemo(() => {
        const lengths = groups
            .map((group) => Number(latestConditionReportByGroupId[group?.id]?.length))
            .filter((value) => Number.isFinite(value));

        if (lengths.length === 0) {
            return null;
        }

        return {
            min: Math.min(...lengths),
            max: Math.max(...lengths)
        };
    }, [groups, latestConditionReportByGroupId]);

    const deploymentDateScaleRange = React.useMemo(() => {
        const timestamps = groups
            .map((group) => getDeploymentTimestamp(group))
            .filter((value) => Number.isFinite(value));

        if (timestamps.length === 0) {
            return null;
        }

        return {
            min: Math.min(...timestamps),
            max: Math.max(...timestamps)
        };
    }, [groups]);

    const getLengthScaleColour = (lengthValue) => {
        if (!Number.isFinite(lengthValue) || !lengthScaleRange) {
            return null;
        }

        const ratio = lengthScaleRange.max > lengthScaleRange.min
            ? (lengthValue - lengthScaleRange.min) / (lengthScaleRange.max - lengthScaleRange.min)
            : 0.5;
        const clampedRatio = Math.max(0, Math.min(1, ratio));
        const hue = 282 * clampedRatio;
        const saturation = 82 - (clampedRatio > 0.45 && clampedRatio < 0.65 ? 10 : 0);
        const lightness = 48 + (clampedRatio > 0.12 && clampedRatio < 0.42 ? 4 : 0);
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };

    const getDeploymentDateScaleColour = (group) => {
        const timestamp = getDeploymentTimestamp(group);
        if (!Number.isFinite(timestamp) || !deploymentDateScaleRange) {
            return null;
        }

        const ratio = deploymentDateScaleRange.max > deploymentDateScaleRange.min
            ? (timestamp - deploymentDateScaleRange.min) / (deploymentDateScaleRange.max - deploymentDateScaleRange.min)
            : 0.5;
        const clampedRatio = Math.max(0, Math.min(1, ratio));
        const hue = 4 + (202 * clampedRatio);
        const saturation = clampedRatio > 0.42 && clampedRatio < 0.58 ? 85 : 74;
        const lightness = clampedRatio > 0.42 && clampedRatio < 0.58 ? 78 : 48;
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };

    const formatLengthLegendValue = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed.toFixed(2) : "-";
    };

    const formatDeploymentDateLegendValue = (value) => {
        return Number.isFinite(value) ? dateFormatZeros(new Date(value)) : "-";
    };

    const getGroupSizeLabel = (group) => {
        const latestReportLength = Number(latestConditionReportByGroupId[group?.id]?.length);
        if (Number.isFinite(latestReportLength)) {
            return `${formatLengthLegendValue(latestReportLength)} um`;
        }

        return group?.grade?.description || "No grade";
    };

    const getScreenFillColour = (group) => {
        if (!group) {
            return "#ffffff";
        }

        if (colourMode === "grade") {
            const latestReportLength = Number(latestConditionReportByGroupId[group.id]?.length);
            const lengthScaleColour = getLengthScaleColour(latestReportLength);
            if (lengthScaleColour) {
                return lengthScaleColour;
            }

            return getGradeColour(group) || "#6abf69";
        }

        if (colourMode === "deploymentDate") {
            return getDeploymentDateScaleColour(group) || "#d9d9d9";
        }

        return getBatchColour(group) || "#86b7ff";
    };

    const getLineScreenCount = (line, lineGroups) => {
        const configuredLength = Number(line?.length) || 0;
        const maxGroupEnd = lineGroups.length > 0
            ? Math.max(...lineGroups.map((g) => Number(g?.location?.end) || 0))
            : 0;
        return Math.max(configuredLength, maxGroupEnd, 1);
    };

    const getLineOysterTotal = (lineGroups) => {
        return Math.round(lineGroups.reduce((total, group) => {
            const totalUnits = Number(group?.totalUnits);
            const average = Number(group?.average);
            return total + (Number.isFinite(totalUnits) && Number.isFinite(average)
                ? totalUnits * average
                : 0);
        }, 0));
    };

    const getGroupAtScreen = (lineGroups, screenNo) => {
        return lineGroups.find((group) => {
            const start = Number(group?.location?.start) || 0;
            const end = Number(group?.location?.end) || 0;
            return screenNo >= start && screenNo <= end;
        });
    };

    const getGridDimensions = (screenCount, line) => {
        const unitWidth = getLineInfrastructureWidth(line);
        const sizeLength = getLineInfrastructureSizeLength(line);
        const configuredLength = Math.max(1, Number(line?.length) || screenCount);
        const sizeLengthRows = Number.isFinite(sizeLength)
            ? Math.max(1, Math.ceil(sizeLength))
            : null;

        if (unitWidth <= 1) {
            const preferredRows = sizeLengthRows
                ?? (isMobileViewport ? Math.max(2, Math.ceil(screenCount / 8)) : Math.max(1, Math.ceil(screenCount / 12)));
            const rows = Math.max(1, Math.min(screenCount, preferredRows));
            const cols = Math.max(1, Math.ceil(screenCount / rows));
            return { cols, rows };
        }

        const tilePitch = fixedScreenSize + fixedScreenGap;
        const viewportPaddingAllowance = isMobileViewport ? 132 : 240;
        const availableViewportWidth = Math.max(240, viewportWidth - viewportPaddingAllowance);
        const viewportCols = Math.max(2, Math.floor((availableViewportWidth + fixedScreenGap) / tilePitch));

        if (sizeLengthRows) {
            const rows = Math.max(1, Math.min(screenCount, sizeLengthRows));
            const cols = Math.max(2, Math.min(screenCount, Math.min(viewportCols, Math.ceil(screenCount / rows))));
            return { cols, rows: Math.max(1, Math.ceil(screenCount / cols)) };
        }

        const preferredRows = isMobileViewport
            ? Math.max(2, Math.min(4, Math.ceil(configuredLength / 12)))
            : Math.max(2, Math.min(3, Math.ceil(configuredLength / 20)));
        const minColsFromRows = Math.ceil(screenCount / preferredRows);
        const lengthDrivenCols = Math.ceil(configuredLength * (isMobileViewport ? 0.6 : 0.75));
        const widthBiasCols = Math.max(0, Math.round((unitWidth - 1) * (isMobileViewport ? 1 : 2)));
        const preferredCols = Math.max(minColsFromRows, lengthDrivenCols + widthBiasCols);
        const cols = Math.max(2, Math.min(screenCount, Math.min(viewportCols, preferredCols)));
        const rows = Math.max(1, Math.ceil(screenCount / cols));
        return { cols, rows };
    };

    const getLineInfrastructureWidth = (line) => {
        const raw = line?.infrastructure?.unitWidth
            ?? line?.infrastructure?.UnitWidth
            ?? line?.unitWidth
            ?? line?.UnitWidth
            ?? 1;
        const value = Number(raw);
        return Number.isFinite(value) && value > 0 ? value : 1;
    };

    const getLineInfrastructureSizeWidth = (line) => {
        const raw = line?.infrastructure?.sizeWidth
            ?? line?.infrastructure?.SizeWidth
            ?? line?.sizeWidth
            ?? line?.SizeWidth;
        const value = Number(raw);
        return Number.isFinite(value) && value > 0 ? value : null;
    };

    const getLineInfrastructureSizeLength = (line) => {
        const raw = line?.infrastructure?.sizeLength
            ?? line?.infrastructure?.SizeLength
            ?? line?.sizeLength
            ?? line?.SizeLength;
        const value = Number(raw);
        return Number.isFinite(value) && value > 0 ? value : null;
    };

    const getLineRenderStyle = (line, screenCount) => {
        const unitWidth = getLineInfrastructureWidth(line);
        const sizeWidth = getLineInfrastructureSizeWidth(line);
        const isRoundTank = unitWidth <= 1;
        const { cols, rows } = getGridDimensions(screenCount, line);
        const borderWidth = 4;
        const titleHeight = isMobileViewport ? 46 : 52;
        const titleGap = isMobileViewport ? 10 : 14;
        const gridWidth = cols * fixedScreenSize + Math.max(0, cols - 1) * fixedScreenGap;
        const gridHeight = rows * fixedScreenSize + Math.max(0, rows - 1) * fixedScreenGap;

        if (isRoundTank) {
            const paddingX = isMobileViewport ? 18 : 22;
            const paddingTop = isMobileViewport ? 16 : 20;
            const paddingBottom = isMobileViewport ? 16 : 20;
            const diameter = Math.max(
                150,
                gridWidth + paddingX * 2 + borderWidth * 2,
                gridHeight + paddingTop + paddingBottom + titleHeight + titleGap + borderWidth * 2
            );

            return {
                outerWidth: diameter,
                outerHeight: diameter,
                borderRadius: "50%",
                gridWidth,
                gridHeight,
                cols,
                gap: fixedScreenGap,
                screenSize: fixedScreenSize,
                screenBorderRadius: sizeWidth === 1 ? "50%" : 0,
                paddingX,
                paddingTop,
                paddingBottom,
                titleGap,
                titleVariant: isMobileViewport ? "subtitle1" : "h5"
            };
        }

        const paddingX = isMobileViewport ? 18 : 28;
        const paddingTop = isMobileViewport ? 12 : 14;
        const paddingBottom = isMobileViewport ? 14 : 16;
        const outerWidth = Math.max(
            isMobileViewport ? 250 : 360,
            gridWidth + paddingX * 2 + borderWidth * 2
        );
        const outerHeight = Math.max(
            isMobileViewport ? 116 : 124,
            gridHeight + paddingTop + paddingBottom + titleHeight + titleGap + borderWidth * 2
        );

        return {
            outerWidth,
            outerHeight,
            borderRadius: 6,
            gridWidth,
            gridHeight,
            cols,
            gap: fixedScreenGap,
            screenSize: fixedScreenSize,
            screenBorderRadius: sizeWidth === 1 ? "50%" : 0,
            paddingX,
            paddingTop,
            paddingBottom,
            titleGap,
            titleVariant: isMobileViewport ? "subtitle1" : "h5"
        };
    };

    const getSelectionBounds = (start, end) => {
        if (end === null || end === undefined) {
            return { start, end: start };
        }
        const low = Math.min(start, end);
        const high = Math.max(start, end);
        return { start: low, end: high };
    };

    const getSelectionSummary = (lineGroups, start, end) => {
        const bounds = getSelectionBounds(start, end);
        const screens = [];
        for (let screenNo = bounds.start; screenNo <= bounds.end; screenNo += 1) {
            screens.push({ screenNo, group: getGroupAtScreen(lineGroups, screenNo) });
        }

        const grouped = screens.filter((x) => !!x.group);
        const empty = screens.length - grouped.length;
        const uniqueGroupIds = [...new Set(grouped.map((x) => x.group.id))];
        const onlyGroup = uniqueGroupIds.length === 1 ? grouped[0]?.group : null;

        return {
            ...bounds,
            screens,
            count: screens.length,
            groupedCount: grouped.length,
            emptyCount: empty,
            isAllEmpty: grouped.length === 0,
            isAllGrouped: grouped.length === screens.length,
            uniqueGroupIds,
            onlyGroup,
            canPickup: grouped.length === screens.length,
            canDeploy: grouped.length === 0
        };
    };

    const getPickupSegments = (lineGroups, start, end) => {
        const bounds = getSelectionBounds(start, end);
        const selectedGroups = lineGroups.filter((group) => {
            const groupStart = Number(group?.location?.start) || 0;
            const groupEnd = Number(group?.location?.end) || 0;
            return groupEnd >= bounds.start && groupStart <= bounds.end;
        });

        return selectedGroups.map((group) => {
            const groupStart = Number(group?.location?.start) || 0;
            const groupEnd = Number(group?.location?.end) || 0;
            return {
                groupId: group.id,
                startPos: Math.max(bounds.start, groupStart),
                endPos: Math.min(bounds.end, groupEnd)
            };
        }).filter((segment) => !!segment.groupId && segment.startPos <= segment.endPos);
    };

    const isScreenWithinSelection = (lineId, screenNo) => {
        if (!screenSelection || screenSelection.lineId !== lineId) {
            return false;
        }
        if (screenSelection.end === null || screenSelection.end === undefined) {
            return screenNo === screenSelection.start;
        }
        const bounds = getSelectionBounds(screenSelection.start, screenSelection.end);
        return screenNo >= bounds.start && screenNo <= bounds.end;
    };

    const closeSelectionTooltip = () => {
        setTooltipPosition(null);
        setTooltipLine(null);
        setScreenSelection(null);
    };

    React.useEffect(() => {
        if (!tooltipPosition) {
            return undefined;
        }

        const onDocumentPointerDown = (event) => {
            const target = event.target;
            if (selectionPopoverContentRef.current?.contains(target)) {
                return;
            }

            if (target?.closest?.(".MuiPickersPopper-root, .MuiPopper-root")) {
                return;
            }

            if (target?.closest?.("[data-screen-key]")) {
                return;
            }

            closeSelectionTooltip();
        };

        document.addEventListener("pointerdown", onDocumentPointerDown, true);
        return () => document.removeEventListener("pointerdown", onDocumentPointerDown, true);
    }, [tooltipPosition]);

    const getScreenBoxRect = (lineId, screenNo) => {
        const el = document.querySelector(`[data-screen-key="line-${lineId}-screen-${screenNo}"]`);
        return el ? el.getBoundingClientRect() : null;
    };

    const onScreenClick = (event, line, lineGroups, screenNo) => {
        setTooltipLine({ line, lineGroups });

        setScreenSelection((previous) => {
            let newSelection;
            if (!previous || previous.lineId !== line.id) {
                newSelection = { lineId: line.id, start: screenNo, end: null };
            } else {
                const hasRange = previous.end !== null && previous.end !== undefined && previous.start !== previous.end;
                if (hasRange) {
                    newSelection = { lineId: line.id, start: screenNo, end: null };
                } else {
                    const nextSummary = getSelectionSummary(lineGroups, previous.start, screenNo);
                    if (!nextSummary.isAllEmpty && !nextSummary.isAllGrouped) {
                        newSelection = { lineId: line.id, start: screenNo, end: null };
                    } else {
                        newSelection = { lineId: line.id, start: previous.start, end: screenNo };
                    }
                }
            }

            // Anchor tooltip to the top of the lowest-numbered selected screen
            const anchorScreen = newSelection.end !== null && newSelection.end !== undefined
                ? Math.min(newSelection.start, newSelection.end)
                : newSelection.start;
            const anchorRect = getScreenBoxRect(line.id, anchorScreen);
            const refRect = anchorRect ?? event.currentTarget.getBoundingClientRect();
            setTooltipPosition({
                top: refRect.top - 12,
                left: refRect.left + refRect.width / 2
            });

            return newSelection;
        });
    };

    const currentSelectionSummary = (() => {
        if (!screenSelection || !tooltipLine || screenSelection.lineId !== tooltipLine.line.id) {
            return null;
        }
        if (screenSelection.end === null || screenSelection.end === undefined) {
            return null;
        }
        return getSelectionSummary(tooltipLine.lineGroups, screenSelection.start, screenSelection.end);
    })();

    const firstSelectedGroup = (() => {
        if (!screenSelection || !tooltipLine || screenSelection.lineId !== tooltipLine.line.id) {
            return null;
        }
        return getGroupAtScreen(tooltipLine.lineGroups, screenSelection.start);
    })();

    const firstSelectedScreenNo = (() => {
        if (!screenSelection || !tooltipLine || screenSelection.lineId !== tooltipLine.line.id) {
            return null;
        }
        return screenSelection.start;
    })();

    const firstSelectedScreenIsEmpty = (() => {
        return !!screenSelection
            && !!tooltipLine?.line
            && screenSelection.lineId === tooltipLine.line.id
            && (screenSelection.end === null || screenSelection.end === undefined)
            && firstSelectedScreenNo !== null
            && !firstSelectedGroup;
    })();

    const isSelectingRangeEnd = (() => {
        return !!screenSelection
            && !!tooltipLine?.line
            && screenSelection.lineId === tooltipLine.line.id
            && (screenSelection.end === null || screenSelection.end === undefined);
    })();

    const renderGroupInfo = (group, showRange = false) => {
        if (!group) {
            return null;
        }

        const averageValue = Number(group.average);

        return (
            <Box
                sx={{
                    mt: 0.5,
                    p: 1,
                    border: "1px solid #111",
                    borderRadius: 1,
                    backgroundColor: "#fafafa"
                }}
            >
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {group.cropLabel || "Unknown"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {getGroupSizeLabel(group)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                    Deployed {getDeploymentDateLabel(group)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                   {Number.isFinite(averageValue) ? averageValue : "Unknown"} pcs/screen
                </Typography>
                {showRange ? (
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                        Occupies screens {group.location?.start ?? "?"}-{group.location?.end ?? "?"}
                    </Typography>
                ) : null}
            </Box>
        );
    };

    const refreshHatcheryState = () => {
        setReloadToken((prev) => prev + 1);
    };

    const pickupSegmentsForRange = async (lineGroups, start, end) => {
        const pickupSegments = getPickupSegments(lineGroups, start, end);
        if (pickupSegments.length === 0) {
            return false;
        }

        let allSucceeded = true;
        for (const segment of pickupSegments) {
            const response = await postApi("/Farms/Groups/Pickup", {
                GroupId: segment.groupId,
                StartPos: segment.startPos,
                EndPos: segment.endPos,
                EntireBatch: false,
                Date: textfieldDateFormat(actionDate)
            });

            if (!response.ok) {
                allSucceeded = false;
                break;
            }
        }

        return allSucceeded;
    };

    const pickupSelection = async () => {
        if (!currentSelectionSummary?.canPickup || !tooltipLine?.lineGroups) {
            return;
        }

        setLoading(true);
        try {
            const allSucceeded = await pickupSegmentsForRange(
                tooltipLine.lineGroups,
                currentSelectionSummary.start,
                currentSelectionSummary.end
            );

            if (allSucceeded) {
                closeSelectionTooltip();
                refreshHatcheryState();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const pickupSingleScreen = async () => {
        if (!tooltipLine?.lineGroups || !firstSelectedScreenNo || !firstSelectedGroup) {
            return;
        }

        setLoading(true);
        try {
            const allSucceeded = await pickupSegmentsForRange(
                tooltipLine.lineGroups,
                firstSelectedScreenNo,
                firstSelectedScreenNo
            );

            if (allSucceeded) {
                closeSelectionTooltip();
                refreshHatcheryState();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const startDeployFromSelection = () => {
        if (!currentSelectionSummary?.canDeploy || !tooltipLine?.line?.id) {
            return;
        }

        const lineId = tooltipLine.line.id;
        const deployRange = {
            lineId,
            start: currentSelectionSummary.start,
            end: currentSelectionSummary.end,
            totalUnits: currentSelectionSummary.count
        };

        closeSelectionTooltip();
        setPendingDeployRange({
            ...deployRange
        });
        setOpenDeployModal(true);
    };

    const startDeployFromSingleScreen = () => {
        if (!tooltipLine?.line?.id || !firstSelectedScreenIsEmpty) {
            return;
        }

        const deployRange = {
            lineId: tooltipLine.line.id,
            start: firstSelectedScreenNo,
            end: firstSelectedScreenNo,
            totalUnits: 1
        };

        closeSelectionTooltip();
        setPendingDeployRange({
            ...deployRange
        });
        setOpenDeployModal(true);
    };

    const closeDeployModal = () => {
        setOpenDeployModal(false);
        setPendingDeployRange(null);
    };

    const startConditionReportFromSelection = () => {
        const selectedGroupIds = currentSelectionSummary?.canPickup
            ? (currentSelectionSummary.uniqueGroupIds || []).filter((groupId) => !!groupId)
            : [];

        if (selectedGroupIds.length === 0) {
            return;
        }

        setPendingConditionGroupIds(selectedGroupIds);
        setOpenConditionReportModal(true);
    };

    const startConditionReportFromSingleScreen = () => {
        if (!firstSelectedGroup?.id) {
            return;
        }

        setPendingConditionGroupIds([firstSelectedGroup.id]);
        setOpenConditionReportModal(true);
    };

    const closeConditionReportModal = () => {
        setOpenConditionReportModal(false);
        setPendingConditionGroupIds([]);
    };

    const selectDeployGroup = async (group) => {
        if (!group?.id || !pendingDeployRange?.lineId) {
            closeDeployModal();
            return;
        }

        const selectedUnits = Number(pendingDeployRange.totalUnits) || 0;
        const availableUnits = Number(group.totalUnits);
        const deployUnits = Number.isFinite(availableUnits) && availableUnits >= 0
            ? Math.min(selectedUnits, availableUnits)
            : selectedUnits;
        const deployEndpoint = farm?.isHatchery === true
            ? "/Farms/Groups/DeployHatchery"
            : "/Farms/Groups/Deploy";

        setLoading(true);
        try {
            const response = await postApi(deployEndpoint, {
                GroupId: group.id,
                LineId: pendingDeployRange.lineId,
                StartPos: pendingDeployRange.start,
                TotalUnits: deployUnits,
                Date: textfieldDateFormat(actionDate)
            });

            if (response.ok) {
                closeSelectionTooltip();
                closeDeployModal();
                refreshHatcheryState();
            } else {
                closeDeployModal();
            }
        } catch (error) {
            console.error(error);
            closeDeployModal();
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <FormModal open={openDeployModal} handleClose={closeDeployModal} title={"Select Group To Deploy"} style={{ zIndex: 100000 }}>
                <OnShoreDisplay
                    select
                    access={props.access}
                    handleClose={closeDeployModal}
                    setBatch={selectDeployGroup}
                    farm={{ warehouseId: farm?.warehouseId, isHatchery: true }}
                />
            </FormModal>

            <HatcheryConditionReportModal
                open={openConditionReportModal}
                handleClose={closeConditionReportModal}
                groupIds={pendingConditionGroupIds}
                groups={groups}
                onSaved={refreshHatcheryState}
            />

            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
                    <SelectFarmWarehouse
                        setState={setFarm}
                        state={farm}
                        access={props.access}
                        filterWarehouse={(warehouse) => warehouse?.isHatchery === true}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={6} xl={9} style={{ textAlign: "right" }}>
                    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: { xs: "flex-start", md: "flex-end" }, width: "100%" }}>
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            value={colourMode}
                            onChange={(event, mode) => {
                                if (mode) {
                                    setColourMode(mode);
                                }
                            }}
                            aria-label="screen colour mode"
                        >
                            <ToggleButton value="batch">
                                <div style={{ whiteSpace: "nowrap" }}>Batch Colour</div>
                            </ToggleButton>
                            <ToggleButton value="grade">
                                <div style={{ whiteSpace: "nowrap" }}>Grade Colour</div>
                            </ToggleButton>
                            <ToggleButton value="deploymentDate">
                                <div style={{ whiteSpace: "nowrap" }}>Deployment Date</div>
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {colourMode === "grade" ? (
                            <Box sx={{ mt: 1, width: { xs: "100%", sm: 260 }, maxWidth: 320 }}>
                               
                                <Box
                                    sx={{
                                        height: 16,
                                        borderRadius: 99,
                                        border: "1px solid #757575",
                                        background: lengthScaleGradient
                                    }}
                                />
                                {lengthScaleRange ? (
                                    <Stack direction="row" sx={{ mt: 0.5, justifyContent: "space-between" }}>
                                        <Typography variant="caption">{formatLengthLegendValue(lengthScaleRange.min)} um</Typography>
                                        <Typography variant="caption">{formatLengthLegendValue(lengthScaleRange.max)} um</Typography>
                                    </Stack>
                                ) : (
                                    <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "text.secondary" }}>
                                        No condition-report length values available.
                                    </Typography>
                                )}
                            </Box>
                            ) : colourMode === "deploymentDate" ? (
                                <Box sx={{ mt: 1, width: { xs: "100%", sm: 260 }, maxWidth: 320 }}>
                                    <Box
                                        sx={{
                                            height: 16,
                                            borderRadius: 99,
                                            border: "1px solid #757575",
                                            background: deploymentDateScaleGradient
                                        }}
                                    />
                                    {deploymentDateScaleRange ? (
                                        <Stack direction="row" sx={{ mt: 0.5, justifyContent: "space-between" }}>
                                            <Typography variant="caption">{formatDeploymentDateLegendValue(deploymentDateScaleRange.min)}</Typography>
                                            <Typography variant="caption">{formatDeploymentDateLegendValue(deploymentDateScaleRange.max)}</Typography>
                                        </Stack>
                                    ) : (
                                        <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "text.secondary" }}>
                                            No deployment dates available.
                                        </Typography>
                                    )}
                                </Box>
                        ) : null}
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <Stack spacing={2}>
                            {leases.map((lease) => {
                                const leaseLines = sortLines(linesByLease[lease.id] || []);
                                return (
                                    <Box
                                        key={`lease-${lease.id}`}
                                        sx={{
                                            border: "4px solid #111",
                                            p: 2,
                                            backgroundColor: "#f6f6f6"
                                        }}
                                    >
                                        <Typography variant="h4" sx={{ mb: 2 }}>
                                            {leaseLabel(lease)}
                                        </Typography>

                                        {leaseLines.length === 0 ? (
                                            <Typography variant="body2">No active lines.</Typography>
                                        ) : (
                                            <Box
                                                sx={{
                                                    overflowX: "auto",
                                                    pb: 1
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: 3,
                                                        alignItems: "flex-start"
                                                    }}
                                                >
                                                    {leaseLines.map((line) => {
                                                        const lineGroups = sortGroupScreens(groupsByLine[line.id] || []);
                                                        const screenCount = getLineScreenCount(line, lineGroups);
                                                        const oysterTotal = getLineOysterTotal(lineGroups);
                                                        const lineRenderStyle = getLineRenderStyle(line, screenCount);
                                                        const screenIndexes = Array.from({ length: screenCount }, (_, i) => i + 1);
                                                        return (
                                                            <Box
                                                                key={`line-${line.id}`}
                                                                sx={{
                                                                    width: lineRenderStyle.outerWidth,
                                                                    height: lineRenderStyle.outerHeight,
                                                                    display: "flex",
                                                                    justifyContent: "flex-start",
                                                                    alignItems: "flex-start",
                                                                    flex: "0 0 auto"
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: lineRenderStyle.outerWidth,
                                                                        height: lineRenderStyle.outerHeight,
                                                                        boxSizing: "border-box",
                                                                        border: "4px solid #111",
                                                                        borderRadius: lineRenderStyle.borderRadius,
                                                                        px: `${lineRenderStyle.paddingX}px`,
                                                                        pt: `${lineRenderStyle.paddingTop}px`,
                                                                        pb: `${lineRenderStyle.paddingBottom}px`,
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        backgroundColor: "#fff"
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            mb: `${lineRenderStyle.titleGap}px`,
                                                                            textAlign: "center"
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant={lineRenderStyle.titleVariant}
                                                                            sx={{ lineHeight: 1.15 }}
                                                                        >
                                                                            {lineLabel(line)}
                                                                        </Typography>
                                                                        {oysterTotal > 0 ? (
                                                                            <Typography variant="body2" sx={{ mt: 0.25, color: "text.secondary" }}>
                                                                                {oysterTotal.toLocaleString()} oys
                                                                            </Typography>
                                                                        ) : null}
                                                                    </Box>
                                                                    <Box
                                                                        sx={{
                                                                            width: lineRenderStyle.gridWidth,
                                                                            height: lineRenderStyle.gridHeight,
                                                                            display: "grid",
                                                                            gridTemplateColumns: `repeat(${lineRenderStyle.cols}, ${lineRenderStyle.screenSize}px)`,
                                                                            gridAutoRows: `${lineRenderStyle.screenSize}px`,
                                                                            gap: `${lineRenderStyle.gap}px`,
                                                                            justifyItems: "center",
                                                                            alignItems: "center",
                                                                            overflow: "hidden",
                                                                            alignContent: "center",
                                                                            justifyContent: "center"
                                                                        }}
                                                                    >
                                                                        {screenIndexes.map((screenNo) => {
                                                                            const group = getGroupAtScreen(lineGroups, screenNo);
                                                                            const isFilled = !!group;
                                                                            const isSelected = isScreenWithinSelection(line.id, screenNo);
                                                                            return (
                                                                                <Box
                                                                                    key={`line-${line.id}-screen-${screenNo}`}
                                                                                    data-screen-key={`line-${line.id}-screen-${screenNo}`}
                                                                                    onClick={(event) => onScreenClick(event, line, lineGroups, screenNo)}
                                                                                    sx={{
                                                                                        width: `${lineRenderStyle.screenSize}px`,
                                                                                        height: `${lineRenderStyle.screenSize}px`,
                                                                                        borderRadius: lineRenderStyle.screenBorderRadius,
                                                                                        border: isSelected ? "2px solid #c62828" : "1.3px solid #111",
                                                                                        backgroundColor: isFilled ? getScreenFillColour(group) : "#fff",
                                                                                        opacity: isFilled ? 1 : 0.65,
                                                                                        cursor: "pointer"
                                                                                    }}
                                                                                    title={isFilled
                                                                                        ? `Screen ${screenNo} • Group ${group.id} (${group.location?.start ?? "?"}-${group.location?.end ?? "?"})`
                                                                                        : `Screen ${screenNo} • Empty`}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}

                            {leases.length === 0 ? <Typography variant="body2">No leases for this hatchery warehouse.</Typography> : null}
                        </Stack>
                    )}
                </Grid>
            </Grid>

            {tooltipPosition ? (
                <Box
                    ref={selectionPopoverContentRef}
                    sx={{
                        position: "fixed",
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: "translate(-50%, -100%)",
                        zIndex: 1300,
                        p: 1.5,
                        minWidth: 260,
                        maxWidth: 340,
                        backgroundColor: "background.paper",
                        borderRadius: 1,
                        boxShadow: 6
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                        {tooltipLine?.line ? (
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {lineLabel(tooltipLine.line)}
                            </Typography>
                        ) : null}

                        {currentSelectionSummary?.canPickup ? (
                            <Button size="small" variant="outlined" onClick={startConditionReportFromSelection}>
                                Record Condition Report
                            </Button>
                        ) : isSelectingRangeEnd && !!firstSelectedGroup ? (
                            <Button size="small" variant="outlined" onClick={startConditionReportFromSingleScreen}>
                                Record Condition Report
                            </Button>
                        ) : null}
                    </Box>

                    {currentSelectionSummary ? (
                        <React.Fragment>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Screens {currentSelectionSummary.start}-{currentSelectionSummary.end} ({currentSelectionSummary.count})
                            </Typography>


                            {currentSelectionSummary.canPickup ? (
                                <React.Fragment>
                                    {renderGroupInfo(currentSelectionSummary.onlyGroup)}
                                    {!currentSelectionSummary.onlyGroup && currentSelectionSummary.uniqueGroupIds.length > 1 ? (
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            Includes {currentSelectionSummary.uniqueGroupIds.length} groups.
                                        </Typography>
                                    ) : null}
                                    <Box sx={{ mt: 1, mb: 0.5 }}>
                                    <DatePicker date={actionDate} setDate={setActionDate} label="Action Date" />
                                </Box>
                                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                                        <Button size="small" variant="contained" onClick={pickupSelection}>
                                            Pickup on {dateFormatZeros(actionDate)}
                                        </Button>
                                    </Stack>
                                </React.Fragment>
                            ) : null}

                            {currentSelectionSummary.canDeploy ? (
                                <React.Fragment>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        Selected screens are empty.
                                    </Typography>
                                    <Box sx={{ mt: 1, mb: 0.5 }}>
                                    <DatePicker date={actionDate} setDate={setActionDate} label="Action Date" />
                                </Box>
                                    <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={startDeployFromSelection}>
                                        Deploy on {dateFormatZeros(actionDate)}
                                    </Button>
                                </React.Fragment>
                            ) : null}

                            {!currentSelectionSummary.canPickup && !currentSelectionSummary.canDeploy ? (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Selection contains mixed occupancy. Click the next start screen to begin again.
                                </Typography>
                            ) : null}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {isSelectingRangeEnd
                                    ? `Start screen ${screenSelection.start} selected. Click another screen to choose end.`
                                    : "Select a screen range to manage this line."}
                            </Typography>

                            {isSelectingRangeEnd ? (
                                firstSelectedGroup ? (
                                    <React.Fragment>
                                        {renderGroupInfo(firstSelectedGroup, true)}
                                        <Box sx={{ mt: 1, mb: 0.5 }}>
                                            <DatePicker date={actionDate} setDate={setActionDate} label="Action Date" />
                                        </Box>
                                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                                            <Button size="small" variant="contained" onClick={pickupSingleScreen}>
                                                Pickup
                                            </Button>
                                        </Stack>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            Selected screen is empty.
                                        </Typography>
                                        <Box sx={{ mt: 1, mb: 0.5 }}>
                                            <DatePicker date={actionDate} setDate={setActionDate} label="Action Date" />
                                        </Box>
                                        <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={startDeployFromSingleScreen}>
                                            Deploy
                                        </Button>
                                    </React.Fragment>
                                )
                            ) : null}
                        </React.Fragment>
                    )}
                </Box>
            ) : null}
        </React.Fragment>
    );
};

export default Hatchery;
